import { LibraryExercise } from '../../../../block/types';
import { CreateEditExerciseMetrics } from './types';

/**
 * Decide whether a parsed metric value should be shown as-is (tempo, a
 * configured keyword, or a complex reps value) rather than having its units
 * stripped for display. Keywords apply to both `reps` and `intensity`, since
 * both can hold a non-numeric keyword value.
 */
export const shouldDisplayValueAsIs = (
  metricType: string,
  value: string,
  keywords: string[] | undefined,
  dataType: unknown,
): boolean => {
  if (metricType === 'tempo') return true;
  if (metricType === 'reps' || metricType === 'intensity') {
    return !!keywords?.includes(value) || dataType === 'complex';
  }
  // Keyword values on any other metric type (e.g. custom metrics with
  // numericType 'none') are meaningful text — stripping units would erase them.
  return keywords?.includes(value) || dataType === 'keyword';
};

/**
 * Determine how many set inputs a newly added metric should have.
 *
 * Uses the minimum input count across already-selected metrics so a new metric
 * lines up with the existing ones. When nothing is selected yet,
 * Math.min(...[]) returns Infinity (which makes Array.from({ length }) throw a
 * RangeError), so we fall back to the actual number of sets.
 */
export const getNumOfSetsForNewMetric = (
  selectedMetrics: CreateEditExerciseMetrics[],
  numOfSetsFallback: number,
): number => {
  if (selectedMetrics.length === 0) {
    return numOfSetsFallback;
  }
  return Math.min(...selectedMetrics.map((m) => m.inputsForSet.length));
};

export const initializeMetrics = (
  defaultMetrics: LibraryExercise['defaultMetrics'],
  existingMetrics: CreateEditExerciseMetrics[],
  getUnitsForMetric: (metricId: string) => string[],
  editingMetrics = false,
): CreateEditExerciseMetrics[] => {
  // If editingMetrics is true, return all existingMetrics
  if (editingMetrics) {
    return existingMetrics;
  }

  // there should always be at least one set
  let maxSetIndex = 1;
  const hasExistingMetrics = existingMetrics && existingMetrics.length > 0;
  if (hasExistingMetrics) {
    maxSetIndex = Math.max(
      ...existingMetrics.map((metric) => metric.inputsForSet.length),
    );
  }

  return (
    defaultMetrics
      .sort((a, b) => a.sortOrder - b.sortOrder)
      // We won't support sets metric in tablet for now
      .filter((m) => m.metric.type !== 'sets')
      .map((defaultMetric) => {
        const metric = defaultMetric.metric;

        let existingMetric;
        if (hasExistingMetrics) {
          existingMetric = existingMetrics.find(
            (m) => m.metricId === metric.id,
          );
        }
        const defaultUnit = getUnitsForMetric(metric.id)?.[0] ?? '';
        return {
          metricId: metric.id,
          name: metric.name,
          sortOrder: defaultMetric.sortOrder,
          type: metric.type,
          exerciseMetricId: existingMetric?.exerciseMetricId,
          inputsForSet:
            existingMetric?.inputsForSet ||
            Array.from({ length: maxSetIndex }, (_, i) => ({
              setIndex: i,
              value: '',
              unit: defaultUnit,
            })),
        };
      })
  );
};
